// =============================================================================
// AttackPreview.ts — Miniatura animada do card de seleção: o personagem
// demonstra a arma inicial em cima de inimigos de mentirinha (issue #6).
// Canvas 2D puro com os sprites procedurais de sprites.ts; todos os cards
// compartilham um único requestAnimationFrame.
// =============================================================================
import type { CharacterDef } from '../../../shared/characters';
import { WEAPON_TABLE, type WeaponKind } from '../../../shared/weapons';
import { DEFAULT_SKIN } from '../../../shared/skins';
import { spriteFactory, skinSpriteId } from '../sprites';
import { SHEET_CHAR_IDS, skinSheetCanvas } from '../charSheets';

// ---------------------------------------------------------------------------
// Imagens dos atlas (sheet do personagem, sprites de fx) pro canvas 2D do
// card — carregam assíncrono; até lá o preview usa o pixel art procedural
// ---------------------------------------------------------------------------
const imgCache = new Map<string, HTMLImageElement>();

function atlasImg(url: string): HTMLImageElement | null {
  let img = imgCache.get(url);
  if (!img) {
    img = new Image();
    img.src = url;
    imgCache.set(url, img);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}

// Palco lógico (o CSS estica mantendo o pixel crocante)
const W = 160;
const H = 68;

// Posições fixas: candidato à esquerda, dupla de sacos de pancada à direita
const PX = 30, PY = 38;
const FOES: Array<{ x: number; y: number }> = [
  { x: 112, y: 34 },
  { x: 138, y: 44 },
];

// Quem apanha na demonstração: dupla de urnas possuídas (o único inimigo
// do jogo desde que o resto do bestiário foi cassado por feiura)

// Sprite do projétil na demonstração, por arma (default: pombinha)
const BOUNCE_PROJ: Record<string, string> = {
  fake_news: 'proj_fake',
  carteira_trabalho: 'proj_carteira',
  cruz_credo: 'proj_cruz',
  vampiros_madrugada: 'proj_vampiro',
};

// Duração do loop (ms) por tipo de arma
const PERIOD: Record<WeaponKind, number> = {
  cone: 1500, bounce: 2000, lob: 2200, wave: 1800, gust: 1800, dash: 2000, convert: 2600,
  trap: 2200, summon: 2400, orbit: 2400, leech: 2600, drain: 2000, nova: 1800, freeze: 2200,
};

// ---------------------------------------------------------------------------
// Ticker único para todos os previews vivos
// ---------------------------------------------------------------------------
const active = new Set<AttackPreview>();
let rafId = 0;

function tick(now: number): void {
  for (const p of active) p.draw(now);
  rafId = active.size ? requestAnimationFrame(tick) : 0;
}

function wake(): void {
  if (!rafId && active.size) rafId = requestAnimationFrame(tick);
}

// progresso 0..1 dentro da janela [a, b] (clampado)
const win = (t: number, a: number, b: number): number =>
  Math.min(1, Math.max(0, (t - a) / (b - a)));

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export class AttackPreview {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly tipo: WeaponKind;
  private readonly cor: string;
  private readonly charId: string;
  private readonly wid: string;
  private readonly player: HTMLCanvasElement;
  private readonly foes: HTMLCanvasElement[];

  constructor(canvas: HTMLCanvasElement, ch: CharacterDef, private readonly skinId: string = DEFAULT_SKIN) {
    canvas.width = W;
    canvas.height = H;
    this.ctx = canvas.getContext('2d')!;
    this.wid = ch.weapon;
    this.tipo = WEAPON_TABLE[ch.weapon].tipo;
    this.cor = '#' + ch.cor.toString(16).padStart(6, '0');
    this.charId = ch.id;
    this.player = spriteFactory.canvas(skinSpriteId(ch.id, skinId));
    this.foes = [spriteFactory.canvas('urna_possuida'), spriteFactory.canvas('urna_possuida')];
    active.add(this);
    wake();
  }

  dispose(): void {
    active.delete(this);
  }

  draw(now: number): void {
    const cv = this.ctx.canvas;
    if (!cv.isConnected || !cv.offsetParent) return; // card fora da tela: não gasta
    const g = this.ctx;
    const t = (now % PERIOD[this.tipo]) / PERIOD[this.tipo];

    g.imageSmoothingEnabled = false;
    g.clearRect(0, 0, W, H);
    g.fillStyle = '#1c1c26'; // chão da Esplanada
    g.fillRect(0, H - 8, W, 2);

    // dança de idle: todo mundo quica em 2 frames, fases diferentes
    const bob = (fase: number) => (Math.floor(now / 260 + fase) % 2);

    switch (this.tipo) {
      case 'cone': this.drawCone(t); break;
      case 'bounce':
        if (this.wid === 'carteira_trabalho') this.drawBoomerang(t);
        else this.drawBounce(t);
        break;
      case 'lob': this.drawLob(t); break;
      case 'wave': this.drawWave(t); break;
      case 'gust': this.drawGust(t); break;
      case 'dash': this.drawDash(t); break;
      case 'convert': this.drawConvert(t, now); break;
      case 'trap': this.drawTrap(t); break;
      case 'orbit': this.drawOrbit(now); break;
      case 'leech': this.drawLeech(t); break;
      default: this.drawWave(t); break; // fallback pra armas fora do elenco inicial
    }

    this.drawPlayer(now, bob(0));
    FOES.forEach((f, i) => {
      const hitT = this.foeHitT(t, i);
      const dx = this.foeKnockX(t, i);
      this.drawFoe(i, f.x + dx, f.y + bob(i + 1), now);
      if (hitT > 0) this.spark(f.x + dx, f.y - 6, 5 + 3 * hitT);
    });

    this.overlays(t, now);
  }

  // Candidato do card: quem tem atlas (charSheets) aparece com o sprite novo
  // andando pra baixo; os demais seguem no chibi procedural. Com skin (issue
  // #24), o atlas recolorido da skin entra no lugar.
  private drawPlayer(now: number, bobY: number): void {
    const g = this.ctx;
    let sheet: HTMLImageElement | HTMLCanvasElement | null = null;
    if (SHEET_CHAR_IDS.includes(this.charId)) {
      sheet = this.skinId === DEFAULT_SKIN
        ? atlasImg(`/assets/${this.charId}_sheet.png`)
        : skinSheetCanvas(this.charId, this.skinId);
    }
    if (!sheet) {
      this.sprite(this.player, PX, PY + bobY, 2);
      return;
    }
    // Ciclo de caminhada frontal: passo, parado, passo, parado (linha 0 do atlas)
    const col = [0, 1, 2, 1][Math.floor(now / 170) % 4];
    const size = 46;
    g.imageSmoothingEnabled = false;
    g.drawImage(sheet, col * 64, 0, 64, 64, PX - size / 2, PY + bobY - size / 2 - 2, size, size);
  }

  // Urna possuída apanhando: atlas desenhado (fx/urna_monster, ciclo de
  // caminhada) quando carregado; senão o pixel art procedural
  private drawFoe(i: number, x: number, y: number, now: number): void {
    const g = this.ctx;
    const atlas = atlasImg('/assets/fx/urna_monster.png');
    if (atlas) {
      const cell = atlas.naturalHeight; // strip horizontal de células quadradas
      const col = Math.floor(now / 160 + i * 2) % Math.floor(atlas.naturalWidth / cell);
      const size = 34;
      g.imageSmoothingEnabled = false;
      g.drawImage(atlas, col * cell, 0, cell, cell, x - size / 2, y - size / 2, size, size);
      return;
    }
    this.sprite(this.foes[i], x, y, 2, true);
  }

  // --------------------------------------------------------------------------
  // Animações por tipo de arma (t normalizado 0..1)
  // --------------------------------------------------------------------------

  // Soluço Patriótico / cones: leque de "HIC!" varrendo os inimigos
  private drawCone(t: number): void {
    const p = win(t, 0.12, 0.62);
    if (p <= 0 || p >= 1) return;
    const g = this.ctx;
    const alpha = Math.sin(p * Math.PI);
    const reach = lerp(30, 105, Math.min(1, p * 2.2));
    for (const [spread, a] of [[0.52, 0.30], [0.30, 0.25]] as const) {
      g.globalAlpha = alpha * a;
      g.fillStyle = this.cor;
      g.beginPath();
      g.moveTo(PX + 8, PY);
      g.arc(PX + 8, PY, reach, -spread, spread);
      g.closePath();
      g.fill();
    }
    g.globalAlpha = 1;
  }

  // Faz o L / bounce: projétil ricocheteando de inimigo em inimigo
  private drawBounce(t: number): void {
    const path: Array<[number, number, number, number, number, number]> = [
      // [t0, t1, x0, y0, x1, y1]
      [0.05, 0.35, PX + 10, PY - 4, FOES[0].x, FOES[0].y - 4],
      [0.35, 0.60, FOES[0].x, FOES[0].y - 4, FOES[1].x, FOES[1].y - 2],
      [0.60, 0.85, FOES[1].x, FOES[1].y - 2, W + 14, 6],
    ];
    for (const [t0, t1, x0, y0, x1, y1] of path) {
      const p = win(t, t0, t1);
      if (p <= 0 || p >= 1) continue;
      const x = lerp(x0, x1, p);
      const y = lerp(y0, y1, p) - Math.sin(p * Math.PI) * 10; // arco do voo
      this.trail(x, y, x0, y0);
      this.sprite(spriteFactory.canvas(BOUNCE_PROJ[this.wid] ?? 'proj_dove'), x, y, 1.5);
    }
  }

  // Carteira do Marçal / bumerangue: atravessa a dupla girando e volta
  private drawBoomerang(t: number): void {
    const g = this.ctx;
    const out = win(t, 0.06, 0.44);
    const back = win(t, 0.46, 0.86);
    if ((out <= 0 || out >= 1) && (back <= 0 || back >= 1)) return;
    const x0 = PX + 10, x1 = FOES[1].x + 14;
    const x = back > 0 ? lerp(x1, x0, back) : lerp(x0, x1, out);
    const y = PY - 4 + (back > 0 ? 6 : 0); // ida por cima, volta por baixo
    const carteira = spriteFactory.canvas('proj_carteira');
    g.save();
    g.translate(x, y);
    g.rotate(t * Math.PI * 8); // girando feito bumerangue de CLT
    g.drawImage(carteira, -carteira.width * 0.75, -carteira.height * 0.75, carteira.width * 1.5, carteira.height * 1.5);
    g.restore();
  }

  // Cruz Credo / orbit: rosário de cruzes girando em volta do candidato
  // (metade de cima da elipse fica translúcida pra sugerir profundidade)
  private drawOrbit(now: number): void {
    const g = this.ctx;
    const cruz = spriteFactory.canvas('proj_cruz');
    for (let b = 0; b < 3; b++) {
      const ang = now / 320 + (b / 3) * Math.PI * 2;
      const x = PX + Math.cos(ang) * 34;
      const y = PY + Math.sin(ang) * 20; // órbita achatada (pseudo-3D)
      const atras = Math.sin(ang) < 0;
      g.globalAlpha = atras ? 0.55 : 1;
      this.sprite(cruz, x, y, atras ? 1.1 : 1.3);
      g.globalAlpha = 1;
    }
  }

  // Vampiros do Temer / leech: morcego voa, GRUDA mordendo e volta pro gabinete
  private drawLeech(t: number): void {
    const f = FOES[0];
    const bat = spriteFactory.canvas('proj_vampiro');
    const out = win(t, 0.05, 0.28);
    const back = win(t, 0.78, 0.96);
    let x: number, y: number;
    if (out > 0 && out < 1) {
      x = lerp(PX + 10, f.x, out);
      y = lerp(PY - 6, f.y - 10, out) - Math.sin(out * Math.PI) * 8;
    } else if (t >= 0.28 && t < 0.78) {
      // grudado no pescoço, tremendo de sede
      x = f.x + (Math.floor(t * 42) % 2 ? 1 : -1);
      y = f.y - 11;
    } else if (back > 0 && back < 1) {
      x = lerp(f.x, PX + 10, back);
      y = lerp(f.y - 10, PY - 6, back) - Math.sin(back * Math.PI) * 8;
    } else {
      return;
    }
    this.sprite(bat, x, y, 1.4);
  }

  // Pão com Mortadela / Cadeirada / lob: o objeto voa em parábola e vira poça
  private drawLob(t: number): void {
    const g = this.ctx;
    const cadeira = this.wid === 'cadeirada';
    const cx = (FOES[0].x + FOES[1].x) / 2;
    const cy = (FOES[0].y + FOES[1].y) / 2 + 12;
    const p = win(t, 0.06, 0.34); // objeto em voo
    if (p > 0 && p < 1) {
      const x = lerp(PX + 10, cx, p);
      const y = lerp(PY - 8, cy - 4, p) - Math.sin(p * Math.PI) * 22; // parábola (gordurosa ou televisiva)
      g.save();
      g.translate(x, y);
      g.rotate(p * Math.PI * 3); // girando no ar
      const fallback = spriteFactory.canvas(cadeira ? 'proj_cadeira' : 'proj_pao');
      g.drawImage(fallback, -fallback.width * 0.75, -fallback.height * 0.75, fallback.width * 1.5, fallback.height * 1.5);
      g.restore();
    }
    // Poça de mortadela / campo de lascas (some no fim do loop)
    const pool = win(t, 0.34, 0.95);
    if (pool > 0 && pool < 1) {
      const fade = Math.min(1, win(t, 0.34, 0.42) * 1, 1 - win(t, 0.82, 0.95));
      g.globalAlpha = 0.35 * fade;
      g.fillStyle = cadeira ? '#b0692f' : '#e07a8a';
      g.beginPath();
      g.ellipse(cx, cy, 34, 10, 0, 0, Math.PI * 2);
      g.fill();
      g.globalAlpha = 0.8 * fade;
      g.strokeStyle = cadeira ? '#b0692f' : '#e07a8a';
      g.lineWidth = 2;
      g.beginPath();
      g.ellipse(cx, cy, lerp(10, 38, win(t, 0.34, 0.5)), lerp(3, 11, win(t, 0.34, 0.5)), 0, 0, Math.PI * 2);
      g.stroke();
      g.globalAlpha = 1;
    }
  }

  // wave (tipo aposentado do elenco inicial): anéis de onda com knockback
  private drawWave(t: number): void {
    const g = this.ctx;
    for (const k of [0, 0.14]) {
      const p = win(t, 0.1 + k, 0.6 + k);
      if (p <= 0 || p >= 1) continue;
      g.globalAlpha = (1 - p) * 0.8;
      g.strokeStyle = this.cor;
      g.lineWidth = 3;
      g.beginPath();
      g.arc(PX, PY, lerp(6, 92, p), -0.9, 0.9);
      g.stroke();
    }
    g.globalAlpha = 1;
  }

  // Estoque de Vento / gust: rajada quase invisível varre a fileira (#121)
  private drawGust(t: number): void {
    const p = win(t, 0.1, 0.55);
    if (p <= 0 || p >= 1) return;
    const g = this.ctx;
    const x = lerp(PX + 6, W + 20, p);
    g.globalAlpha = 0.7 * Math.sin(p * Math.PI);
    g.strokeStyle = '#cfe8f4'; // linhas de vento branco-azuladas
    g.lineWidth = 2;
    for (const dy of [-10, -2, 8]) {
      g.beginPath();
      g.moveTo(x - 30, PY + dy);
      g.quadraticCurveTo(x - 12, PY + dy - 4, x, PY + dy);
      g.stroke();
    }
    // folhinhas voando na rajada
    g.fillStyle = '#9fd4a8';
    g.fillRect(x - 8, PY - 8 + Math.sin(t * 20) * 3, 3, 3);
    g.fillRect(x - 18, PY + 5 + Math.cos(t * 16) * 3, 2, 2);
    g.globalAlpha = 1;
  }

  // Motociata / dash: a moto fantasma atropela a fileira
  private drawDash(t: number): void {
    const p = win(t, 0.12, 0.55);
    if (p <= 0 || p >= 1) return;
    const g = this.ctx;
    const x = lerp(PX + 6, W + 26, p);
    const y = FOES[0].y + 6;
    g.globalAlpha = 0.55;
    g.strokeStyle = '#8a8f98'; // rastro de fumaça de diesel subsidiado
    g.lineWidth = 2;
    for (const dy of [-6, 0, 6]) {
      g.beginPath();
      g.moveTo(x - 34, y + dy);
      g.lineTo(x - 12, y + dy);
      g.stroke();
    }
    g.globalAlpha = 1;
    this.sprite(spriteFactory.canvas('proj_moto'), x, y, 1.5);
  }

  // Toma Lá Dá Cá / convert: emenda voa, inimigo troca de lado na hora
  private drawConvert(t: number, now: number): void {
    const g = this.ctx;
    const f = FOES[0];
    const p = win(t, 0.05, 0.30); // dinheiro público em trânsito
    if (p > 0 && p < 1) {
      const x = lerp(PX + 10, f.x, p);
      const y = lerp(PY - 6, f.y - 4, p) - Math.sin(p * Math.PI) * 14;
      g.fillStyle = '#f7d117';
      g.fillRect(x - 3, y - 2, 6, 4);
      g.fillStyle = '#2ecc71';
      g.fillRect(x - 2, y - 1, 4, 2);
    }
    if (t > 0.32 && t < 0.96) {
      // aura verde de aliado comprado + coraçãozinho fisiológico
      g.globalAlpha = 0.35 + 0.1 * Math.sin(now / 120);
      g.fillStyle = '#2ecc71';
      g.beginPath();
      g.ellipse(f.x, f.y + 17, 14, 4, 0, 0, Math.PI * 2);
      g.fill();
      g.globalAlpha = 1;
      g.fillStyle = '#2ecc71';
      const hy = f.y - 26 - Math.floor(now / 300 % 2);
      g.fillRect(f.x - 4, hy, 3, 3);
      g.fillRect(f.x + 1, hy, 3, 3);
      g.fillRect(f.x - 3, hy + 2, 6, 3);
      g.fillRect(f.x - 1, hy + 5, 2, 2);
    }
  }

  // Raízes da Amazônia / trap (#121): cipós brotam do chão prendendo a dupla
  private drawTrap(t: number): void {
    const g = this.ctx;
    const p = win(t, 0.08, 0.9);
    if (p <= 0 || p >= 1) return;
    const cx = (FOES[0].x + FOES[1].x) / 2;
    const cy = (FOES[0].y + FOES[1].y) / 2 + 12;
    const fade = Math.min(1, win(t, 0.08, 0.2) * 1, (1 - win(t, 0.8, 0.95)));
    g.globalAlpha = 0.30 * fade;
    g.fillStyle = '#1e9e4e';
    g.beginPath();
    g.ellipse(cx, cy, 34, 10, 0, 0, Math.PI * 2);
    g.fill();
    // raízes SUBINDO do chão (a floresta cobra na raiz)
    const grow = win(t, 0.12, 0.28);
    g.globalAlpha = 0.85 * fade;
    g.strokeStyle = '#5a8f3c';
    g.lineWidth = 2;
    for (let i = 0; i < 5; i++) {
      const bx = cx - 28 + i * 14;
      const sway = (i % 2 ? 3 : -3);
      g.beginPath();
      g.moveTo(bx, cy + 4);
      g.quadraticCurveTo(bx + sway, cy - 14, bx, lerp(cy + 4, cy - 34, grow));
      g.stroke();
    }
    g.globalAlpha = 1;
  }

  // --------------------------------------------------------------------------
  // Quando cada inimigo faisca (levou dano) e quanto é empurrado, por arma
  // --------------------------------------------------------------------------
  private foeHitT(t: number, i: number): number {
    const flick = (a: number, b: number) => {
      const p = win(t, a, b);
      return p > 0 && p < 1 ? Math.sin(p * Math.PI) : 0;
    };
    switch (this.tipo) {
      case 'cone': return flick(0.24, 0.55);
      case 'bounce':
        if (this.wid === 'carteira_trabalho') {
          // bumerangue assina na ida E na volta
          return i === 0
            ? flick(0.16, 0.26) + flick(0.68, 0.78)
            : flick(0.3, 0.4) + flick(0.54, 0.64);
        }
        return i === 0 ? flick(0.32, 0.44) : flick(0.57, 0.69);
      case 'lob': return flick(0.34, 0.46) + flick(0.55, 0.65) + flick(0.72, 0.82);
      case 'wave': return flick(0.34, 0.5);
      case 'gust': return i === 0 ? flick(0.28, 0.4) : flick(0.36, 0.48);
      case 'dash': return i === 0 ? flick(0.3, 0.42) : flick(0.38, 0.5);
      case 'convert': return i === 1 ? flick(0.5, 0.62) + flick(0.72, 0.84) : 0;
      case 'trap': return flick(0.4, 0.5) + flick(0.6, 0.7) + flick(0.78, 0.88);
      case 'orbit': return flick(0.15, 0.3) + flick(0.5, 0.65) + flick(0.85, 0.98);
      case 'leech': return i === 0 ? flick(0.3, 0.42) + flick(0.46, 0.58) + flick(0.62, 0.74) : 0;
      default: return flick(0.3, 0.5);
    }
  }

  private foeKnockX(t: number, i: number): number {
    if (this.tipo === 'wave') {
      const p = win(t, 0.34, 0.8);
      if (p > 0 && p < 1) return Math.sin(p * Math.PI) * (9 - i * 3);
    }
    // Estoque de Vento: a rajada empurra a dupla junto (#121)
    if (this.tipo === 'gust') {
      const p = win(t, 0.3, 0.75);
      if (p > 0 && p < 1) return Math.sin(p * Math.PI) * (10 - i * 3);
    }
    if (this.tipo === 'dash' && this.foeHitT(t, i) > 0) return 3;
    return 0;
  }

  // Extras desenhados por cima dos bonecos
  private overlays(t: number, now: number): void {
    // convert: o aliado comprado devolve o investimento atirando no colega
    if (this.tipo === 'convert' && t > 0.45 && t < 0.9) {
      const g = this.ctx;
      const blink = Math.floor(now / 140) % 2 === 0;
      if (blink) {
        g.strokeStyle = '#2ecc71';
        g.lineWidth = 2;
        g.beginPath();
        g.moveTo(FOES[0].x + 8, FOES[0].y);
        g.lineTo(FOES[1].x - 8, FOES[1].y);
        g.stroke();
      }
    }
  }

  // --------------------------------------------------------------------------
  // Primitivas
  // --------------------------------------------------------------------------

  // desenha um sprite centrado em (x, y); flip espelha pra encarar a esquerda
  private sprite(src: HTMLCanvasElement, x: number, y: number, scale: number, flip = false): void {
    const g = this.ctx;
    const w = src.width * scale, h = src.height * scale;
    g.save();
    g.translate(x, y);
    if (flip) g.scale(-1, 1);
    g.drawImage(src, -w / 2, -h / 2, w, h);
    g.restore();
  }

  // faísca de dano: estrelinha amarela/branca
  private spark(x: number, y: number, r: number): void {
    const g = this.ctx;
    g.strokeStyle = '#fff3a0';
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(x - r, y); g.lineTo(x + r, y);
    g.moveTo(x, y - r); g.lineTo(x, y + r);
    g.stroke();
    g.strokeStyle = '#ffffff';
    g.lineWidth = 1;
    const d = r * 0.6;
    g.beginPath();
    g.moveTo(x - d, y - d); g.lineTo(x + d, y + d);
    g.moveTo(x - d, y + d); g.lineTo(x + d, y - d);
    g.stroke();
  }

  // rastro do projétil ricocheteante
  private trail(x: number, y: number, fromX: number, fromY: number): void {
    const g = this.ctx;
    const ang = Math.atan2(y - fromY, x - fromX);
    g.globalAlpha = 0.4;
    g.strokeStyle = '#f4f4f4';
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(x - Math.cos(ang) * 12, y - Math.sin(ang) * 12);
    g.lineTo(x - Math.cos(ang) * 4, y - Math.sin(ang) * 4);
    g.stroke();
    g.globalAlpha = 1;
  }
}
